import { ReactNode } from 'react'
import { Box, Typography } from '@mui/material'
import { C100Row } from '../../_types/carnivore100.type'
import { C100Tokens, sectorColor } from './c100-tokens'
import { fmtMarketCap } from '../../pages/carnivore100/carnivore100.util'
import RankBadge from './rank-badge'
import SparkCell from './spark-cell'
import {
  AnalystCell, ObOsCell, ScoreCell, ScreenChip, StreakCell, TrendLabelCell
} from './signal-cells'

const MONO = "'IBM Plex Mono', monospace"

const fmtPct = (n: number | null) => (n === null ? '—' : `${n >= 0 ? '+' : ''}${n.toFixed(2)}%`)

function PctText({ value, t }: { value: number | null; t: C100Tokens }) {
  const color = value === null ? t.muted : value >= 0 ? t.gain : t.loss
  return (
    <Typography
      sx={{
        fontFamily: MONO,
        fontSize: 13,
        color,
        fontVariantNumeric: 'tabular-nums',
        display: 'flex',
        alignItems: 'center',
        gap: 0.25
      }}
    >
      {value !== null && <span>{value >= 0 ? '▲' : '▼'}</span>}
      {fmtPct(value)}
    </Typography>
  )
}

// One renderer per column id. Keyed this way so the row can be rendered in
// whatever order the user has dragged the columns into.
export const CELL_RENDERERS: Record<string, (r: C100Row, t: C100Tokens) => ReactNode> = {
  rank: (r, t) => (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
      <Typography
        sx={{
          fontFamily: MONO,
          fontSize: 13,
          fontWeight: 600,
          color: t.text,
          fontVariantNumeric: 'tabular-nums',
          minWidth: 22
        }}
      >
        {r.rank}
      </Typography>
      <RankBadge rankDelta={r.rankDelta} isNew={r.isNew} />
    </Box>
  ),

  ticker: (r, t) => (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
      <Box
        sx={{
          width: 8,
          height: 8,
          borderRadius: '50%',
          bgcolor: sectorColor(r.sector),
          flexShrink: 0
        }}
      />
      <Typography
        sx={{
          fontFamily: MONO,
          fontSize: 13,
          fontWeight: 600,
          color: t.text,
          letterSpacing: '0.04em'
        }}
      >
        {r.ticker}
      </Typography>
      <ScreenChip count={r.screenCount} />
    </Box>
  ),

  score: r => <ScoreCell value={r.score} emphasis />,

  name: (r, t) => (
    <Typography
      sx={{
        fontSize: 13,
        color: t.text,
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap'
      }}
    >
      {r.name}
    </Typography>
  ),

  sector: (r, t) => (
    <>
      <Typography
        sx={{ fontSize: 12, fontWeight: 500, color: sectorColor(r.sector), lineHeight: 1.2 }}
      >
        {r.sector || '—'}
      </Typography>
      <Typography
        sx={{
          fontSize: 10,
          color: t.muted,
          mt: 0.1,
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap'
        }}
      >
        {r.industry}
      </Typography>
    </>
  ),

  market: (r, t) => (
    <Box
      sx={{
        display: 'inline-block',
        fontFamily: MONO,
        fontSize: 10,
        textTransform: 'uppercase',
        letterSpacing: '0.04em',
        color: t.muted,
        border: `1px solid ${t.hairline}`,
        borderRadius: '6px',
        px: '6px',
        py: '1px',
        lineHeight: 1.6
      }}
    >
      {r.market || '—'}
    </Box>
  ),

  price: (r, t) => (
    <Typography
      sx={{ fontFamily: MONO, fontSize: 13, color: t.text, fontVariantNumeric: 'tabular-nums' }}
    >
      {r.price != null ? `$${r.price.toFixed(2)}` : '—'}
    </Typography>
  ),

  marketCap: (r, t) => (
    <Typography
      sx={{
        fontFamily: MONO,
        fontSize: 12.5,
        color: r.marketCap == null ? t.muted : t.text,
        fontVariantNumeric: 'tabular-nums',
        whiteSpace: 'nowrap'
      }}
    >
      {fmtMarketCap(r.marketCap)}
    </Typography>
  ),

  dayChangePct: (r, t) => <PctText value={r.dayChangePct} t={t} />,
  timeframeChangePct: (r, t) => <PctText value={r.timeframeChangePct} t={t} />,

  tech: r => <ScoreCell value={r.tech} />,
  fund: r => <ScoreCell value={r.fund} />,
  mom: r => <ScoreCell value={r.mom} />,
  obOs: r => <ObOsCell value={r.obOs} />,
  upStreakDays: r => <StreakCell days={r.upStreakDays} />,
  trendLabel: r => <TrendLabelCell label={r.trendLabel} />,
  analyst: r => <AnalystCell value={r.analyst} />,
  spark: r => <SparkCell values={r.sparkline} />
}
